import React from 'react'
/*
 *  Generic Components
 */
import InputField from './views/Input'
import TextAreaField from './views/Textarea'
import SuccessModal from './views/SuccessModal'
import './index.scss'
import {
  ErrorMessageComponent,
  PleaseNoteComponent,
  SendEmailButtonComponent,
} from './views/StatelessComponent'

/*
 * Helper functions
 */
import { isValidEmail, formatPhoneNumber } from '../../../utils'
import { CONSTs, getData } from './views/utils'

// ID is based on the article that is displayed above the contact form.
// if that article ID changes then we need to change this as well.
const CONVERSATION_ARTICLE_ID = 'lets-start-the-conversation-today-article-document'

/*
 * Contact Brokerage Form component
 */

interface FormComponentProps {
  callback?: (...args: any[]) => any
  name?: string
  content: any
  componentId?: string
  brandExtension?: boolean
}

interface FormData {
  firstName: string | null
  lastName: string | null
  emailAddress: string | null
  financialIns: string | null
  interest: string | null
  phone: string | null
  showInfo: boolean
}

interface ErrorFields {
  firstName: boolean
  lastName: boolean
  emailAddress: boolean
  emailWrongFormat: boolean
  financialIns: boolean
  interest: boolean
  phone: boolean
}

interface FormComponentState {
  showServiceFailedMessage: boolean
  showSuccessMessage: boolean
  loading: boolean
  formData: FormData
  errorFields: ErrorFields
}

export default class FormComponent extends React.Component<FormComponentProps, FormComponentState> {
  private firstNameRef: React.RefObject<HTMLInputElement | null>
  private lastNameRef: React.RefObject<HTMLInputElement | null>
  private emailaddressRef: React.RefObject<HTMLInputElement | null>
  private financialInsRef: React.RefObject<HTMLInputElement | null>
  private phoneRef: React.RefObject<HTMLInputElement | null>
  private interestRef: React.RefObject<HTMLTextAreaElement | null>

  constructor(props: FormComponentProps) {
    super(props)
    this.state = {
      showServiceFailedMessage: false,
      showSuccessMessage: false,
      loading: false,
      formData: {
        firstName: null,
        lastName: null,
        emailAddress: null,
        financialIns: null,
        interest: null,
        phone: null,
        showInfo: false,
      },
      errorFields: {
        firstName: false,
        lastName: false,
        emailAddress: false,
        emailWrongFormat: false,
        financialIns: false,
        interest: false,
        phone: false,
      },
    }
    /*
     * React refs to access the component
     */
    this.firstNameRef = React.createRef<HTMLInputElement>()
    this.lastNameRef = React.createRef<HTMLInputElement>()
    this.emailaddressRef = React.createRef<HTMLInputElement>()
    this.financialInsRef = React.createRef<HTMLInputElement>()
    this.phoneRef = React.createRef<HTMLInputElement>()
    this.interestRef = React.createRef<HTMLTextAreaElement>()

    /*
     * Binding this(current object) keyword with the methods
     */
    this.submitData = this.submitData.bind(this)
    this.changeState = this.changeState.bind(this)
    this.getFormStatus = this.getFormStatus.bind(this)
    this.handleFieldChange = this.handleFieldChange.bind(this)
    this.renderSubmitButtons = this.renderSubmitButtons.bind(this)
    this.showServerResponse = this.showServerResponse.bind(this)
    this.setEmailErrorMessage = this.setEmailErrorMessage.bind(this)
    this.hide = this.hide.bind(this)
    this.getFormClass = this.getFormClass.bind(this)
  }

  hide() {}

  /*
   * Show service response
   */
  showServerResponse(props: any, message: string) {}

  /*
   * Form status: valid or invalid
   */
  getFormStatus() {
    const { formData } = this.state
    let isFormValid = true
    for (const props in formData) {
      if (
        props !== 'inquiry' &&
        props !== 'phone' /* ignore optional */ &&
        props !== 'showInfo' /* ignore optional */ &&
        (formData as any)[props] === null
      ) {
        isFormValid = false
      }
    }
    return isFormValid
  }

  changeState() {
    // To do: Update the data
  }

  /*
   * Render Buttons
   */
  renderSubmitButtons() {
    const {
      props: {
        content: {
          formLabels: { buttonLoadingLabel },
        },
      },
      state: { loading },
    } = this
    return (
      <div className='ButtonRow'>
        <SendEmailButtonComponent
          callback={this.submitData}
          isLoading={loading}
          defaultLabel='Send email'
          loadingLabel={buttonLoadingLabel}
        />
        {!this.props.componentId && (
          <button
            className='Button Button--tertiary u-sizeFull '
            type='button'
            onClick={this.hide}
            aria-label={'Cancel'}
          >
            {' '}
            Cancel{' '}
          </button>
        )}
      </div>
    )
  }

  /*
   * Validate the form fields
   */
  validateField(fieldName: string, value: string) {
    // To do: update the newly added fields
    let emailValid = this.state.errorFields.emailAddress
    let firstNameValid = this.state.errorFields.firstName
    let lastNameValid = this.state.errorFields.lastName
    let financialInsValid = this.state.errorFields.financialIns
    let interestValid = this.state.errorFields.interest
    const fieldValues = { ...this.state.formData }
    if (!this.state.formData.interest) {
      const interestDefault = this.interestRef.current?.defaultValue ?? ''
      fieldValues.interest = interestDefault
    }
    const fieldErrors = { ...this.state.errorFields }
    const phoneRefNode = this.phoneRef.current as HTMLInputElement | null
    let phoneValid = this.state.errorFields.phone

    switch (fieldName) {
      case 'firstName': {
        firstNameValid = value !== '' || value.length > 0
        fieldValues.firstName = firstNameValid ? value : null
        fieldErrors.firstName = !firstNameValid
        break
      }
      case 'lastName': {
        lastNameValid = value !== '' || value.length > 0
        fieldValues.lastName = lastNameValid ? value : null
        fieldErrors.lastName = !lastNameValid
        break
      }
      case 'financialIns': {
        financialInsValid = value !== '' || value.length > 0
        fieldValues.financialIns = financialInsValid ? value : null
        fieldErrors.financialIns = !financialInsValid
        break
      }
      case 'interest': {
        interestValid = value !== '' || value.length > 0
        fieldValues.interest = interestValid ? value : null
        fieldErrors.interest = !interestValid
        break
      }
      case 'phone': {
        const phoneNumOnly = value.replace(/\D/g, '')
        phoneValid = phoneNumOnly.length > 0
        if (phoneValid && phoneRefNode) {
          if (phoneNumOnly.length < 10) {
            fieldErrors.phone = true
            fieldValues.phone = null
          } else if (phoneNumOnly.length === 10) {
            fieldErrors.phone = false
            phoneRefNode.value = formatPhoneNumber(phoneRefNode.value)
            fieldValues.phone = phoneRefNode.value
          } else {
            phoneRefNode.value = formatPhoneNumber(phoneRefNode.value).substring(0, 14)
            fieldValues.phone = phoneRefNode.value
            fieldErrors.phone = false
          }
        } else {
          fieldErrors.phone = true
          fieldValues.phone = null
        }
        break
      }
      case 'emailAddress': {
        emailValid = isValidEmail(value)
        fieldValues.emailAddress = emailValid ? value : null
        fieldErrors.emailAddress = !emailValid
        fieldErrors.emailWrongFormat = false
        const formEmailAddress = this.emailaddressRef.current?.value
        if (formEmailAddress && formEmailAddress.length > 0) {
          fieldErrors.emailWrongFormat = true
        }
        break
      }
      default:
        ;(fieldValues as any)[fieldName] = value
        break
    }
    if (fieldName && value) {
      this.setState({
        formData: fieldValues,
        errorFields: fieldErrors,
      })
    }
  }

  handleFieldChange(event: React.SyntheticEvent<any>) {
    event.preventDefault()
    const target = event.target as HTMLInputElement | HTMLTextAreaElement
    const name = target.name as keyof FormComponentState | string
    const value = target.value
    this.setState(
      {
        // Preserve original behavior—assigning dynamic key on the component state object
        // (even though it's unused for known keys)
        [name as any]: value as any,
      } as any,
      () => {
        this.validateField(name as string, value)
      }
    )
    // @ts-ignore: keyCode exists on legacy KeyboardEvent targets
    if ((event as any).keyCode === 13) {
      this.validateField(name as string, value)
    }
  }

  /*
   * Form Submission
   */
  async submitData(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault()
  }

  /*
   * Set the error message for email address
   */
  setEmailErrorMessage(wrongEmail: boolean, message: any) {
    return wrongEmail ? message.emailWrongFormat : message.emailAddress
  }

  getFormClass() {
    const { componentId, brandExtension } = this.props
    let classes = ''
    switch (componentId) {
      case 'AFIG Contact Form':
        classes = `u-paddingVert32 u-sm-paddingVert32 u-paddingHoriz48 u-sm-paddingHoriz24 u-marginCenter u-lg-size7of12 u-md-size9of12 u-sm-sizeFull ${
          brandExtension ? 'u-bgBrandExColorLightGray' : 'u-bgColorLight'
        } u-marginBottom48 u-sm-marginBottom24`
        break
      default:
        classes = 'u-sizeConstrained'
        break
    }
    return classes
  }

  render() {
    const {
      props: {
        name,
        content: { formErrorMessages, formPleaseNote, serviceMessages, formLabels },
        brandExtension,
      },
      state: { showServiceFailedMessage, showSuccessMessage, errorFields },
    } = this
    return (
      <div className='u-marginTop8'>
        <div className={this.getFormClass()}>
          <form
            name={name}
            className='afig-contact-business-form'
            onBlur={e => {
              this.handleFieldChange(e)
            }}
            onKeyUp={e => {
              this.handleFieldChange(e)
            }}
          >
            <div className='u-paddingBottom16'>
              <InputField
                name='firstName'
                id='firstName'
                placeholder={formLabels.firstName}
                label='First name'
                reference={this.firstNameRef}
                error={errorFields.firstName}
                message={formErrorMessages.errorFirstName}
                hideStyle='u-hidden'
              />
            </div>

            <div className='u-paddingBottom16'>
              <InputField
                name='lastName'
                id='lastName'
                placeholder={formLabels.lastName}
                label='Last name'
                reference={this.lastNameRef}
                error={errorFields.lastName}
                message={formErrorMessages.errorLastName}
                hideStyle='u-hidden'
              />
            </div>

            <div className='u-paddingBottom16'>
              <InputField
                name='emailAddress'
                id='emailAddress'
                // placeholder={formLabels.emailaddress}
                label='Email'
                reference={this.emailaddressRef}
                error={errorFields.emailAddress}
                message={this.setEmailErrorMessage(errorFields.emailWrongFormat, formErrorMessages)}
                hideStyle='u-hidden'
              />
            </div>

            <div className='u-paddingBottom16'>
              <InputField
                name='phone'
                id='phone'
                placeholder={formLabels.phone}
                label='Phone Number (optional)'
                reference={this.phoneRef}
                hideStyle='u-hidden'
              />
              <label className='afig-contact-business-form-example u-marginTop8'>
                Example: XXX-XXX-XXXX
              </label>
            </div>

            <div className='u-paddingBottom16'>
              <InputField
                name='financialIns'
                id='financialIns'
                placeholder={formLabels.financialIns}
                label='Financial Institution'
                reference={this.financialInsRef}
                error={errorFields.financialIns}
                message={formErrorMessages.financialIns}
                hideStyle='u-hidden'
              />
            </div>

            <TextAreaField
              name='interest'
              id='interest'
              defaultValue=''
              error={errorFields.interest}
              message={formErrorMessages.interest}
              placeholder=''
              label='Key areas of interest'
              reference={this.interestRef}
              row={3}
            />
            {showServiceFailedMessage && serviceMessages.serviceFailed && (
              <ErrorMessageComponent message={serviceMessages.serviceFailed} />
            )}
            <div className='Checkbox u-marginBottom24'>
              <input
                className='Checkbox-button'
                id='showMoreInfo'
                name='showMoreInfo'
                value={true as any}
                type='checkbox'
                onChange={event => {
                  this.setState({
                    ...this.state,
                    formData: {
                      ...this.state.formData,
                      showInfo: (event.target as HTMLInputElement).checked,
                    },
                  })
                }}
                defaultChecked={this.state.formData.showInfo}
              />
              <label htmlFor='showMoreInfo' className='Checkbox-label'>
                I&apos;d like to learn more about recruiting financial advisors
              </label>
            </div>
            {this.renderSubmitButtons()}
          </form>
          {formPleaseNote && formPleaseNote.message && (
            <PleaseNoteComponent content={formPleaseNote.message} />
          )}
        </div>
      </div>
    )
  }
}
export { FormComponent }
